import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, ReactNode } from "react";
import { DialogCloseButton } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * The edge drawer — the person drawer and the add-crew drawer in
 * `Visual - Demo.dc.html`. Same Radix dialog underneath as `Dialog`, but the
 * scrim is unpadded and stretches its panel to the viewport edge instead of
 * centring it.
 *
 * The panel sits on `canvas`, not white: the drawer body is a light-grey column
 * of white cards, and only the header and footer are `surface`. Those two are
 * also the one place the design uses the *outer* `border` colour as a divider
 * rather than `border-inner`.
 *
 * Only `right` occurs in the design; the other three sides are supported for
 * completeness and reuse `shadow-drawer`, which is cast leftwards.
 */
const sheetOverlayVariants = cva(
  "fixed inset-0 z-60 flex bg-ink/50 backdrop-blur-[3px]",
  {
    variants: {
      side: {
        right: "items-stretch justify-end",
        left: "items-stretch justify-start",
        top: "flex-col items-stretch justify-start",
        bottom: "flex-col items-stretch justify-end",
      },
    },
    defaultVariants: { side: "right" },
  },
);

const sheetPanelVariants = cva(
  "relative flex max-w-full flex-col bg-canvas shadow-drawer",
  {
    variants: {
      side: {
        right: "h-full w-[480px]",
        left: "h-full w-[480px]",
        top: "max-h-[86vh] w-full",
        bottom: "max-h-[86vh] w-full",
      },
    },
    defaultVariants: { side: "right" },
  },
);

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetPortal = DialogPrimitive.Portal;
export const SheetClose = DialogPrimitive.Close;

/** The drawer's close control is the same 36px round button as the modal's. */
export const SheetCloseButton = DialogCloseButton;

type SheetContentProps = ComponentProps<typeof DialogPrimitive.Content> &
  VariantProps<typeof sheetPanelVariants> & {
    /** Extra classes for the scrim — most often a higher `z-*` for stacking. */
    overlayClassName?: string;
  };

export function SheetContent({
  className,
  overlayClassName,
  side = "right",
  children,
  ...props
}: SheetContentProps) {
  return (
    <SheetPortal>
      <DialogPrimitive.Overlay
        data-slot="sheet-overlay"
        className={cn(sheetOverlayVariants({ side }), overlayClassName)}
      >
        <DialogPrimitive.Content
          data-slot="sheet-content"
          data-side={side}
          className={cn(sheetPanelVariants({ side }), className)}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Overlay>
    </SheetPortal>
  );
}

type SheetHeaderProps = ComponentProps<"div"> & {
  /** Rendered inside the centred `SheetTitle`. */
  children: ReactNode;
  /** Suppress the trailing spacer when a real action sits on the right. */
  showSpacer?: boolean;
};

/**
 * Close on the left, title optically centred, and a 36px spacer on the right so
 * the title stays centred against the close button.
 */
export function SheetHeader({
  className,
  children,
  showSpacer = true,
  ...props
}: SheetHeaderProps) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        "flex shrink-0 items-center gap-3",
        "border-b-[1.5px] border-border bg-surface px-[22px] py-[18px]",
        className,
      )}
      {...props}
    >
      <SheetCloseButton />
      <SheetTitle className="min-w-0 flex-1 text-center">{children}</SheetTitle>
      {showSpacer && <span aria-hidden className="size-9 shrink-0" />}
    </div>
  );
}

export function SheetTitle({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        "truncate font-display text-[1.05rem] font-bold uppercase tracking-[0.02em] text-ink",
        className,
      )}
      {...props}
    />
  );
}

export function SheetDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-[0.86rem] leading-[1.55] text-muted", className)}
      {...props}
    />
  );
}

export function SheetBody({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-body"
      className={cn("flex-1 overflow-y-auto p-[22px]", className)}
      {...props}
    />
  );
}

/**
 * Sticky action shelf. The add-crew drawer stacks two full-width buttons, the
 * person drawer holds one — hence the column direction.
 */
export function SheetFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        "flex shrink-0 flex-col gap-2.5",
        "border-t-[1.5px] border-border bg-surface px-[22px] py-4",
        className,
      )}
      {...props}
    />
  );
}

export { sheetOverlayVariants, sheetPanelVariants };
export type { SheetContentProps };
